/**
 * CR312: Shared hook for fetching WEB_CRAWL recipe options + S0 default recipe.
 * Used by AddSourceModal, WebRecipeOverrideSection, WebScanTab.
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface RecipeOption {
  id: string;
  recipe_name: string;
  is_template: boolean;
}

export function useWebRecipeOptions() {
  const [recipes, setRecipes] = useState<RecipeOption[]>([]);
  const [s0DefaultRecipeId, setS0DefaultRecipeId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [recipesResult, settingsResult] = await Promise.all([
        supabase
          .from("capture_recipe_templates")
          .select("id, recipe_name, is_template")
          .eq("recipe_scope", "WEB_CRAWL")
          .eq("is_archived", false)
          .order("recipe_name"),
        supabase
          .from("app_scan_settings")
          .select("web_default_recipe_template_id")
          .eq("singleton_id", true)
          .maybeSingle(),
      ]);

      if (!recipesResult.error && recipesResult.data) {
        setRecipes(recipesResult.data as RecipeOption[]);
      }

      if (!settingsResult.error && settingsResult.data) {
        setS0DefaultRecipeId(settingsResult.data.web_default_recipe_template_id ?? null);
      }
    } catch (err) {
      console.error("[useWebRecipeOptions] Load error:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const templateRecipes = recipes.filter((r) => r.is_template);
  const otherRecipes = recipes.filter((r) => !r.is_template);

  return {
    recipes,
    templateRecipes,
    otherRecipes,
    s0DefaultRecipeId,
    isLoading,
    reload: load,
  };
}
